import Icon from '@mdi/react';
import { Stack, SxProps, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { vars } from '../../../Theme';
import AppTooltip, { AppTooltipVariant, TooltipPlacement } from '../AppTooltip/AppTooltip';
import { mdiFlask } from '@mdi/js';
import { useTranslation } from 'react-i18next';

/** Wrapper styles. */
export const wrapperDefaultSx: SxProps = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
};

/** Typography styles. */
export const typographySx: SxProps = {
    fontSize: '16px',
    color: vars.font,
};

/**
 * Base Component for vulnerability-related icons in the application.
 */
export default ({
    path,
    color,
    size,
    tooltipText,
    tooltipPlacement,
    testMode,
    sx,
    withLabel,
    labelSx = {},
    wrapperSx = {},
    label,
    type,
}: IVulnerabilityIconProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'docTable' });

    const getTooltip = testMode ? t('testDocument') : tooltipText;

    /** Create the icon iteself. */
    const icon = (
        <Stack
            sx={[
                {
                    alignItems: 'center',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <Icon
                path={testMode ? mdiFlask : path}
                color={testMode ? vars.testDoc : color ?? vars.font}
                size={size ?? 1}
                style={{ flexGrow: 0, flexShrink: 0 }}
            />
        </Stack>
    );

    /** Wrap the icon in a tooltip, if needed. */
    const appIcon = useMemo(() => {
        return tooltipText ? (
            <AppTooltip title={getTooltip} placement={tooltipPlacement} type={type}>
                {icon}
            </AppTooltip>
        ) : (
            icon
        );
    }, [tooltipText, type, tooltipPlacement, icon]);

    /**Adds the icon's label, if needed. */
    return withLabel ? (
        <Stack sx={{ ...wrapperDefaultSx, ...wrapperSx }}>
            {appIcon}
            <Typography sx={{ ...typographySx, ...labelSx }}>{label}</Typography>
        </Stack>
    ) : (
        appIcon
    );
};

/**
 * Interface the describes the props for a VulnerabilityIcon component.
 */
export interface IVulnerabilityIconProps {
    /** Icon SVG Path. */
    path: string;

    /** (Optional) Icon fill color. */
    color?: string | 'inherit';

    /** (Optional) Icon size. */
    size?: number;

    /** (Optional) Icon Tooltip Text. */
    tooltipText?: string | React.ReactNode;

    /** (Optional) Icon Tooltip Placement. */
    tooltipPlacement?: TooltipPlacement;

    /** (Optional) TestMode property. */
    testMode?: boolean;

    /** (Optional) Extra styles to be applied to the component. */
    sx?: SxProps;

    /** (Optional) Defines Typography component. */
    withLabel?: boolean;

    /** (Optional) Defines Wrapper's extra styles. */
    wrapperSx?: SxProps;

    /** (Optional) Defines Typography's extra styles. */
    labelSx?: SxProps;

    /** (Optional) Defines Typography's text. */
    label?: string;

    /** (Optional) Defines AppToolTip type. */
    type?: AppTooltipVariant;
}
